// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GridContainer, { GridContainerProps } from "@store/MobiusOverrides/GridContainer/GridContainer";
import GridItem, { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import Img from "@store/MobiusOverrides/Img/Img";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";
import { spacingStyles } from "./../../../../../toolkit/src/Themes/common/mixins/spacing";

interface TestimonialModel {
    fields: {
        customerTestimonial: string;
        customerName: string;
        customerTitle: string;
        customerImage: string;
    };
}

const enum fields {
    testimonials = "testimonials",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.testimonials]: TestimonialModel[];
        [fields.title]: string;
    };
}

export interface TestimonialsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    titleItem?: GridItemProps;
    title?: TypographyPresentationProps;
    testimonialItem?: GridItemProps;
    testimonialItemWrapper?: InjectableCss;
    testimonialText?: TypographyPresentationProps;
    customerInfoWrapper?: InjectableCss;
    customerName?: TypographyPresentationProps;
    customerTitle?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    customerImage?: InjectableCss;
}

export const slideshowStyles: TestimonialsStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(30), pxToRem(30))}
            background-color: ${getColor("common.accent")};
        `,
    },
    container: {
        gap: 40,
        css: css`
            ${siteWidthStyles()};
            padding: ${pxToRem(30, 15)};
            color: ${getColor("text.accent")};

            ${breakpoint("md")} {
                padding: ${pxToRem(50, 45)};
            }

            ${breakpoint("xl")} {
                padding: ${pxToRem(50, 60)};
            }
        `,
        offsetCss: css`
            justify-content: center;
        `,
    },
    titleItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    title: {
        variant: "h2",
        forwardAs: "h3",
        color: "primary",
        css: css`
            text-align: center;
        `,
    },
    testimonialItem: {
        width: [12, 12, 4, 4, 4],
    },
    testimonialItemWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            width: 100%;
            padding: ${pxToRem(30)};
            gap: ${pxToRem(20)};
            text-align: center;
            background-color: ${getColor("common.background")};
            border: 1px solid ${getColor("common.borderDark")};

            ${breakpoint("md")} {
                padding: ${pxToRem(60, 40)};
                gap: ${pxToRem(20)};
            }
        `,
    },
    testimonialText: {
        css: css`
            margin-bottom: ${pxToRem(20)};
        `,
    },
    customerInfoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${pxToRem(5)};
        `,
    },
    customerName: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    customerTitle: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    imageWrapper: {
        css: css`
            position: relative;
            width: ${pxToRem(125)};
            height: ${pxToRem(125)};
        `,
    },
    customerImage: {
        css: css`
            position: absolute;
            top: 0;
            border-radius: 50%;
            width: 100%;
            height: 100%;
            object-fit: cover;
        `,
    },
};

const styles = slideshowStyles;

export const Testimonials: FC<OwnProps> = ({ fields: { testimonials, title } }) => {
    if (testimonials.length === 0) {
        return <></>;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.titleItem}>
                    <Typography {...styles.title}>{title}</Typography>
                </GridItem>
                {testimonials.map(testimonial => (
                    <GridItem key={testimonial.fields["customerTestimonial"]} {...styles.testimonialItem}>
                        <StyledWrapper {...styles.testimonialItemWrapper}>
                            <Typography {...styles.testimonialText}>
                                {parse(testimonial.fields["customerTestimonial"], parserOptions)}
                            </Typography>
                            <StyledWrapper {...styles.customerInfoWrapper}>
                                {testimonial.fields["customerName"] && (
                                    <Typography {...styles.customerName}>
                                        {testimonial.fields["customerName"]}
                                    </Typography>
                                )}
                                {testimonial.fields["customerTitle"] && (
                                    <Typography {...styles.customerTitle}>
                                        {testimonial.fields["customerTitle"]}
                                    </Typography>
                                )}
                            </StyledWrapper>
                            {testimonial.fields["customerImage"] && (
                                <StyledWrapper {...styles.imageWrapper}>
                                    <Img src={testimonial.fields["customerImage"]} {...styles.customerImage} />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: Testimonials,
    definition: {
        displayName: "Customer Testimonials",
        group: "Common",
        icon: "file-text",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "What our customers say",
                fieldType: "Translatable",
                tab: contentTab,
            },
            {
                name: fields.testimonials,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { customerName } }) => customerName || "Testimonial",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "customerTestimonial",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: "customerName",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "customerTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "customerImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
